/** Don't save to XHR */

import React, { useContext, useState, useEffect } from 'react';
import { MapContextType } from '@stormwater-software/common';
import { isNull, forIn, get } from 'lodash';
import { Context as InteractionContext } from '../InteractionContext';
import { Context as DataSourceContext } from '../../DataSourceContext';
import { ProjectContext } from '../../../../contexts/ProjectContext';
import { FormContext, FormContextProvider } from '@stormwater-software/lib-form';
import { Context as PositionableInstanceDataContext } from '../../Positionable/InstanceDataContext';
import FormModal from '../../../Shared/form/modal/FormModal';
import ConfirmationModal from '../../../Shared/ConfirmationModal/ConfirmationModal';
import NoteForm from '../../../Commentable/Note/Form';
import FindingsForm, {
  extractFindingData,
  getInitialData,
} from "../../../Inspections/Findings/FindingsForm";
import LegendItemFormSchema, {
  defaultData,
  extractInitialFormData,
  getSchema,
} from '../../../LegendItems/Form/FormSchema';
import { MapEditorRouteContext } from '../../../CustomRoutes/MapEditor';
import { useMap } from '../../useMap';
import {
  parseLatLng,
  pixelToLatLng,
  latLngToPixel,
  isLatLngInMap,
} from '../../../../utils/geo';
import usePositionableSourceModel from '../../hooks/usePositionableSourceModel';
import Loading from '../../../Shared/ResourceIndex/Loading';
import { InspectionContext } from '../../../../contexts/InspectionContext';
import { VersionContextProvider } from '../../../../contexts/VersionContext';
import projectApi from '../../../../utils/api/project';
import clientApi from '../../../../utils/api/client';
import schedulerApi from '../../../../utils/api/inspectionSchedular';
/**
 * Form to create source models.
 *
 * @see {InstanceDataContext} For handling of unsaved source models
 * @type {{ [positionableType: string]: import('react').FunctionComponent }}
 *
 */
const schema = getSchema();

/** GIS gate: "GIS Configuration" feature on, map calibrated and georeferenced. */
const isGisReady = ({ gisEnabled, gisCalibrated, projectUpload }) =>
  Boolean(gisEnabled && gisCalibrated && projectUpload?.geo_top_left_lat != null);

/** [imgW, imgH] of the OL image projection, or nulls when unavailable. */
const getImageSize = (olMap) => {
  const extent = olMap && olMap.getView().getProjection().getExtent();

  return extent ? [extent[2], extent[3]] : [null, null];
};

const forms = {
  findings: ({ inspectionId, initialValue, readOnly, ...props }) => {
    const [init, setInit] = useState(false);
    const inspectionContext = useContext(InspectionContext);
    const projectContext = useContext(ProjectContext);
    const routeContext = useContext(MapEditorRouteContext);
    const { modeData } = useContext(InteractionContext);
    const olMap = useMap();
    const [project, setProject] = useState((projectContext || {}).project);
    const [readOnlyFinding, setReadOnlyFinding] = useState(readOnly);

    useEffect(() => {
      if (initialValue && initialValue.date_completed) {
        setReadOnlyFinding(true);
      }
      const initialData = initialValue
        ? extractFindingData(initialValue, inspectionId)
        : getInitialData(inspectionContext.getDefaultFindingDate());

      // GIS: prefill Location with the coordinates of the right-clicked spot
      const clickCoordinate = modeData?.mapEvent?.coordinate;

      if (!initialValue && clickCoordinate && isGisReady(routeContext)) {
        const [imgW, imgH] = getImageSize(olMap);

        if (imgW && imgH) {
          const [lng, lat] = pixelToLatLng(
            clickCoordinate[0],
            clickCoordinate[1],
            imgW,
            imgH,
            routeContext.projectUpload,
          );

          initialData.location = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
        }
      }

      forIn(initialData, (val, key) => props.onChange(key, val));
      setInit(true);
    }, []);

    useEffect(async () => {
      const url = window.location.href;
      const regex = /projects\/(\d+)\/map/;
      const m = regex.exec(url);

      if (m && m.length > 1) {
        const projectId = m[1];
        const projectDetail = await projectApi.get(projectId);

        setProject(projectDetail);
      }
    }, []);

    return init ? (
      <VersionContextProvider>
        {project && (
          <FindingsForm
            inspectionId={inspectionId}
            readOnly={readOnlyFinding}
            {...props}
            disableFieldRelatedToDateCompleted={
              initialValue && initialValue.date_completed
                ? initialValue.date_completed
                : false
            }
            // SW2-2242. `project.inspection_template_id` was removed by migration 20251001101113
            // (assignment moved to the project_inspection_templates junction), so the fallback
            // resolved to undefined. Dropped rather than re-pointed at the junction: FindingsForm
            // already recovers from a missing templateID by reading it off the inspection.
            templateID={props.inspection?.template_id}
            // Was `'template'` — the whole object, so every `TEMPLATE_NAMES.includes(templateName)`
            // gate in FindingsForm compared against an object and silently failed, hiding Legend
            // Item / Condition / Control Measure in the map editor specifically. FindingsForm now
            // prefers the inspection's own template; this remains the fallback, and has to be the
            // name for that fallback to be worth anything.
            templateName={get(projectContext, 'template.name')}
            modal={false}
            fromFindingAdd={!initialValue}
            isCompleted={
              (initialValue &&
                initialValue.date_completed &&
                !isNull(initialValue.date_completed)) ||
              !isNull(props.inspection?.compliance_date)
            }
          />
        )}
      </VersionContextProvider>
    ) : (
      <Loading />
    );
  },
  control_measure: ({ projectId, projectName, initialValue, ...props }) => (
    <LegendItemFormSchema
      {...props}
      initialValue={
        initialValue
          ? extractInitialFormData(initialValue)
          : {
            ...defaultData(),
            type: "control_measure",

            projectId,
          }
      }
      projectName={projectName}
      canEditType={false}
      schema={schema}
    />
  ),
  pollutant: ({ projectId, projectName, initialValue, ...props }) => (
    <LegendItemFormSchema
      {...props}
      initialValue={
        initialValue
          ? extractInitialFormData(initialValue)
          : {
            ...defaultData(),
            projectId,
            type: "pollutant",
          }
      }
      projectName={projectName}
      canEditType={false}
      schema={schema}
    />
  ),
  commentables: ({ initialValue: sourceModel, ...props }) => (
    <NoteForm
      initialValue={
        sourceModel
          ? Object.assign({}, sourceModel.note, { id: sourceModel.id })
          : { title: new Date().toLocaleDateString() }
      }
      {...props}
    />
  ),
};

// modify form data before passing to the API
const formDataMutators = {
  findings: data => ({
    ...data,
    // default dates
    date_initiated: data.date_initiated ? data.date_initiated : new Date(),
    date_completed: data.date_completed ? data.date_completed : null,
  }),
};

// override onChange handler for specific source model forms
/** @type {{[form: string]: (formContext) => (k: string, v: any) => any}} */
const formOnChangeHandlers = {};

export const positionableTypes = Object.keys(forms);

/**
 * Form and controller to create source model, during map editor
 *
 * @see {InstanceDataContext} and don't save source models to database, at this point!
 */
function SourceModelFormUI({ formProps }) {
  const interactionContext = useContext(InteractionContext);
  const dataSource = useContext(DataSourceContext);
  const formContext = useContext(FormContext);
  const positionableDataContext = useContext(PositionableInstanceDataContext);
  const mapContext = useContext(MapContextType);
  const routeContext = useContext(MapEditorRouteContext);
  const { positionableType, setDefaultMode, setMode } = interactionContext;

  const [pendingFormData, setPendingFormData] = useState(null);

  /**
   * GIS placement for a new finding's Location value:
   * - { coordinate }   → coordinates inside this map, auto-place it
   * - 'out-of-bounds'  → valid coordinates outside this map's corners
   * - null             → GIS placement does not apply (manual placement)
   */
  const getGisPlacement = (location) => {
    if (
      positionableType !== 'findings' ||
      interactionContext.mode !== 'createSourceModel' ||
      interactionContext.modeData?.mapEvent ||
      !isGisReady(routeContext)
    ) {
      return null;
    }
    const coords = parseLatLng(location);

    if (!coords) return null;
    if (!isLatLngInMap(coords.lat, coords.lng, routeContext.projectUpload)) {
      return 'out-of-bounds';
    }
    const [imgW, imgH] = getImageSize(mapContext.map);

    if (!imgW || !imgH) return null;
    const coordinate = latLngToPixel(
      coords.lat,
      coords.lng,
      imgW,
      imgH,
      routeContext.projectUpload,
    );

    return coordinate ? { coordinate } : null;
  };

  const saveSourceModel = (formData, autoPlaceEvent = null) =>
    positionableDataContext
      .createSourceModel(formData, positionableType)
      .then((sourceModel) => {
        mapContext.reloadLayer(positionableType, sourceModel);
        const { modeData } = interactionContext;
        const sourceModelData = isNull(modeData) ? {} : modeData;

        const {
          nextMode = 'create',
          feature = undefined,
          mapEvent = undefined,
        } = sourceModelData;

        setMode(nextMode, {
          sourceModelId: sourceModel.id,
          feature,
          mapEvent: mapEvent || autoPlaceEvent,
        });
      });

  // finding metadata
  const [noMetadataImage, setNoMetadataImage] = useState([]);
  const [showMetadataWarning, setShowMetadataWarning] = useState(false);
  const [isEnabledPhotoMetadata, setIsEnabledPhotoMetadata] = useState(false);  

  const handleSubmitCheckMetadata = (data) => {
    const noMetadataImage = data.images.filter((image) => {
      return image.gpsData === null;
    });

    if (noMetadataImage.length > 0 && isEnabledPhotoMetadata) {
      setNoMetadataImage([...noMetadataImage]);
      setShowMetadataWarning(true);
      return false;
    } else {
      return true;
    }
  }

  if (formContext && positionableType && forms[positionableType] && dataSource.getDataTypes().includes(positionableType)) {
    const FormUI = forms[positionableType];

    const interactionDataProps = isNull(interactionContext.modeData)
      ? {}
      : interactionContext.modeData.props;
    const dataTypeArgs = dataSource.getDataTypeArguments(positionableType) || {};

    return (
      <>
      <FormModal
        onCancel={() => setDefaultMode()}
        onSubmit={data => {
          if(positionableType === "findings" && !handleSubmitCheckMetadata(data)) {
            return;
          }
          const dataMutator =
            formDataMutators[positionableType] || (dat => dat);
          const formData = dataMutator(data);
          const gisPlacement = getGisPlacement(formData.location);

          // GIS: warn before creating a finding 
          if (gisPlacement === 'out-of-bounds') {
            setPendingFormData(formData);

            return;
          }

          return saveSourceModel(formData, gisPlacement);
        }}
        handleClose={() => setDefaultMode()}
        modalProps={{
          title: formProps.initialValue
            ? `Edit ${dataSource.getDataTypeLabel(positionableType, false)}`
            : `Add a ${dataSource.getDataTypeLabel(positionableType, false)}`,
          classes: "positionable-source-model-form",
          ...dataTypeArgs.sourceModelFormModalProps,
        }}
        dismissOnEsc={false}
      >
        <FormUI
          {...formProps}
          {...interactionDataProps}
          onChange={
            formOnChangeHandlers[positionableType]
              ? formOnChangeHandlers[positionableType](formContext)
              : formContext.set
          }
          value={formContext.value}
          {...dataTypeArgs.sourceModelFormProps}
          // finding metadata
          setIsEnabledPhotoMetadata={setIsEnabledPhotoMetadata}
          noMetadataImage={noMetadataImage}
          showMetadataWarning={showMetadataWarning}
          setShowMetadataWarning={setShowMetadataWarning}
        />
      </FormModal>

      <ConfirmationModal
        show={!!pendingFormData}
        title='Location not on this site map'
        subTitle='The coordinates you entered are not within this site map. Create the finding anyway and place it manually, or cancel to change the location.'
        buttonText='Create Finding'
        handleConfirm={() => {
          const formData = pendingFormData;

          setPendingFormData(null);
          saveSourceModel(formData);
        }}
        handleClose={() => setPendingFormData(null)}
      />
      </>
    );
  }

  return null;
}

export default function SourceModelFormWithContext(props) {
  const mapRouteContext = useContext(MapEditorRouteContext);
  const interactionContext = useContext(InteractionContext);
  const sourceModel = usePositionableSourceModel();

  /** @todo set initial value directly on the Context provider for smoother legend item form */
  return (
    <FormContextProvider>
      <SourceModelFormUI
        formProps={{
          projectId: mapRouteContext.projectId,
          projectName: mapRouteContext.projectName,
          inspectionId: mapRouteContext.selectedInspection
            ? mapRouteContext.selectedInspection.id
            : null,
          projectTemplate: mapRouteContext.projectTemplate,
          initialValue:
            interactionContext.mode === "editSourceModel" && sourceModel
              ? sourceModel
              : undefined,
          inspection: mapRouteContext.selectedInspection
        }}
      />
    </FormContextProvider>
  );
}
